import { CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react';
import {
  Toast,
  ToastTitle,
  ToastDescription,
  ToastActions,
  ToastAction,
} from './toast';

export type ToastVariant = 'success' | 'error';

export interface ToastActionButton {
  /** Label text for the action button */
  label: string;
  /** Callback when the action button is clicked */
  onClick: () => void;
  /** Visual variant of the action button */
  variant?: 'default' | 'outline';
}

export interface ToastNotificationProps {
  /** Whether the toast is visible */
  open: boolean;
  /** Callback when toast should be closed */
  onClose: () => void;
  /** Title text displayed in the toast */
  title: string;
  /** Description text displayed below the title */
  description: string;
  /** Visual variant of the toast */
  variant?: ToastVariant;
  /** Position of the toast on screen */
  position?: 'top' | 'bottom';
  /** Optional className for custom styling */
  className?: string;
  /** Optional single action button configuration */
  action?: ToastActionButton;
  /** Optional multiple action buttons configuration (takes precedence over action) */
  actions?: ToastActionButton[];
}

/**
 * A reusable toast notification component that displays temporary messages
 * with support for success and error variants.
 */
export function ToastNotification({
  open,
  onClose,
  title,
  description,
  variant = 'success',
  position = 'bottom',
  className,
  action,
  actions,
}: ToastNotificationProps) {
  const icon =
    variant === 'success' ? (
      <CheckCircleIcon
        size={20}
        weight="fill"
        className="text-green-400 flex-shrink-0"
      />
    ) : (
      <XCircleIcon
        size={20}
        weight="fill"
        className="text-red-400 flex-shrink-0"
      />
    );

  // Use actions array if provided, otherwise fall back to single action
  const actionButtons = actions || (action ? [action] : []);

  const handleActionClick = (actionButton: ToastActionButton) => {
    actionButton.onClick();
    // Optionally close the toast after action is clicked
    // Uncomment the line below if you want auto-close behavior
    // onClose();
  };

  return (
    <Toast
      open={open}
      onClose={onClose}
      position={position}
      className={className}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <ToastTitle>
            <div className="flex items-center gap-2">
              {icon}
              {title}
            </div>
          </ToastTitle>
          <ToastDescription>{description}</ToastDescription>
          {actionButtons.length > 0 && (
            <ToastActions>
              {actionButtons.map((actionButton, index) => (
                <ToastAction
                  key={index}
                  onClick={() => handleActionClick(actionButton)}
                  variant={actionButton.variant || 'default'}
                >
                  {actionButton.label}
                </ToastAction>
              ))}
            </ToastActions>
          )}
        </div>
      </div>
    </Toast>
  );
}

